import { Texture } from 'pixi.js';
import { DevConfig } from './DevConfig';

export const debounce = (func, ms) => {

    let isCooldown = false;

    return function() {
      if (isCooldown) return;

      func.apply(this, arguments);
      isCooldown = true;
      setTimeout(() => isCooldown = false, ms);
    };
}

export const throttle = (func, ms) => {

    let isThrottled = false,
        savedArgs,
        savedThis;

    function wrapper() {
        if (isThrottled) { // (2)
            savedArgs = arguments;
            savedThis = this;
            return;
        }

        func.apply(this, arguments); // (1)

        isThrottled = true;

        setTimeout(function() {
            isThrottled = false; // (3)
            if (savedArgs) {
                wrapper.apply(savedThis, savedArgs);
                savedArgs = savedThis = null;
            }
        }, ms);
    }

    return wrapper;
  }

export const shuffleArray = (array) => {
    let currentIndex = array.length, temporaryValue, randomIndex;

    while (0 !== currentIndex) {
        randomIndex = Math.floor(Math.random() * currentIndex);
        currentIndex -= 1;

        temporaryValue = array[currentIndex];
        array[currentIndex] = array[randomIndex];
        array[randomIndex] = temporaryValue;
    }

    return array;
};

export const remapValue = (value: number, low1: number, high1: number, low2: number, high2: number): number => {
    return low2 + (high2 - low2) * (value - low1) / (high1 - low1);
};

export function searchParam(key: string): string {
    const query = window.location.search.substring(1);
    const params = query.split('&');
    for (var i = 0; i < params.length; i++) {
        var pair = params[i].split('=');
        if (pair[0] === key) {
            return pair[1];
        }
    }
    return '';
}

export function convertRGBA2HEXNUM(r: number, g: number, b: number, a: number = 255): number {
    const hex = '#' +
        ('0' + parseInt(r.toString(), 10).toString(16)).slice(-2) +
        ('0' + parseInt(g.toString(), 10).toString(16)).slice(-2) +
        ('0' + parseInt(b.toString(), 10).toString(16)).slice(-2);

    return parseInt(hex.replace(/^#/, '').substr(0, 6), 16);
}

export async function textureImageByNodules(image): Promise<HTMLCanvasElement> {

    const texture = await Texture.from(image);
    const colors = await textureToJson(texture);
    const palette = [-1, ...new Set<string>(colors.map(pixel => pixel[3] !== 0 ? `rgb(${pixel[0]} ${pixel[1]} ${pixel[2]})` : undefined))];
    const nodules: { [key: string]: HTMLCanvasElement } = {};

    return new Promise((resolve) => {
        const nodule = new Image();
        nodule.addEventListener('load', () => {
            resolve(nodule);
        });
        nodule.src = 'assets/pixel.png';
    })
        .then((nodule: HTMLCanvasElement) => {
            for (let i = 0; i < palette.length; i++) {
                const canvas = document.createElement('canvas');
                canvas.width = nodule.width;
                canvas.height = nodule.height;
                const ctx = canvas.getContext('2d');

                if (!palette[i]) continue;

                ctx.fillStyle = '' + palette[i];
                ctx.globalAlpha = .7;
                ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
                ctx.globalCompositeOperation = "destination-atop";
                ctx.globalAlpha = 1;
                ctx.drawImage(nodule, 0, 0);

                // create a fully green version of img
                // const c = document.createElement('canvas');
                // const cctx = c.getContext('2d');
                // c.width = nodule.width;
                // c.height = nodule.height;
                // cctx.drawImage(nodule, 0, 0);
                // cctx.globalCompositeOperation = 'source-atop';
                // cctx.fillStyle = '' + palette[i];
                // cctx.fillRect(0, 0, nodule.width, nodule.height);
                // cctx.globalCompositeOperation = 'source-over';
                // // draw the grayscale image onto the canvas
                // ctx.drawImage(nodule, 0, 0);
                // // set compositing to color (changes hue with new overwriting colors)
                // ctx.globalCompositeOperation = 'color';
                // // draw the fully green img on top of the grayscale image
                // ctx.drawImage(c, 0, 0);
                // // Always clean up -- change compositing back to default
                // ctx.globalCompositeOperation = 'source-over';

                nodules[palette[i]] = canvas;
            }
            return Promise.resolve(nodule);
        })
        .then((nodule) => {
            const ratio = window.devicePixelRatio;
            const size = nodule.width;

            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = texture.width * size * ratio;
            canvas.height = texture.height * size * ratio;

            for (let i = 0; i < colors.length; i++) {
                const rgb = colors[i];
                const y = i % texture.width;
                const x = (i - y) / texture.width;
                const nod = nodules[`rgb(${rgb[0]} ${rgb[1]} ${rgb[2]})`];
                if (rgb[3] === 0) continue;

                ctx.drawImage(nod, 0, 0, size, size, x * size * ratio, y * size * ratio, size * ratio, size * ratio);
            }

            return Promise.resolve(canvas);
        })
}

export function textureToJson(texture: Texture): number[] {
    const pixels = [];

    const width = texture.baseTexture.width;
    const height = texture.baseTexture.height;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    ctx.drawImage(texture.baseTexture.source, 0, 0);

    for (let x = 0; x < width; x++) {
        for (let y = 0; y < height; y++) {
            const pixel = ctx.getImageData(x, y, 1, 1);
            const data = pixel.data;

            // console.log(data);

            pixels.push([data[0], data[1], data[2], data[3]]);
        }
    }
    return pixels;
}

export function checkMobile(): boolean {
    let isMobile = false;
    if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|BB|PlayBook|IEMobile|Windows Phone|Kindle|Silk|Opera Mini/i.test(navigator.userAgent)) {
        isMobile = true;
    } else {
        isMobile = false;
    }
    const isIpad = navigator.userAgent.match(/iPad/i) != null;
    if (isIpad) this.isMobile = true;

    const ratio = window.devicePixelRatio || 1;
    const screen = {
        width: window.screen.width * ratio,
        height: window.screen.height * ratio
    };
    const iPadPro = (screen.width === 2048 && screen.height === 2732) ||
        (screen.width === 2732 && screen.height === 2048) ||
        (screen.width === 1536 && screen.height === 2048) ||
        (screen.width === 2048 && screen.height === 1536);
    if (iPadPro) {
        this.isMobile = true;
    }

    return isMobile;
}

export function colorIsDark(color: number) {
    const k = DevConfig.PICTURE_GRAYSCALE_IS_BRIGHTER ? 80 : 127.5;
    const r = color >> 16;
    const g = color >> 8 & 255;
    const b = color & 255;

    // yuv-format
    const y = 0.257 * r + 0.504 * g + 0.098 * b + 16;
    return y > k ? false : true;
}

export function secondsToHHMMSS(seconds: number): string {
    return new Date(seconds * 1000).toISOString().substr(11, 8);
}

export function createBase64Image(buffer: Uint8ClampedArray, width, height): string {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = width;
    canvas.height = height;
    ctx.putImageData(new ImageData(buffer, width, height), 0, 0)
    return canvas.toDataURL("image/jpg");
}

export function createBuffer(texture) {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = texture.width;
    canvas.height = texture.height;
    ctx.drawImage(texture.baseTexture.source, 0, 0, canvas.width, canvas.height);
    return ctx.getImageData(0, 0, canvas.width, canvas.height).data;
}

export function createGreyTexture(texture): PIXI.Texture {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = texture.width;
    canvas.height = texture.height;
    ctx.drawImage(texture.baseTexture.source, 0, 0, canvas.width, canvas.height);
    const scannedImageData = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    const greyData = composingAlphaAOverB(transform2Grey(scannedImageData), 0.6, [255, 255, 255]);
    ctx.putImageData(new ImageData(greyData, canvas.width, canvas.height), 0, 0)
    return PIXI.Texture.fromCanvas(canvas, PIXI.SCALE_MODES.NEAREST);
}
export function createPixelizeTexture(texture): PIXI.Texture {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = texture.width;
    canvas.height = texture.height;
    ctx.drawImage(texture.baseTexture.source, 0, 0, canvas.width, canvas.height);
    const scannedImageData = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    // const greyData = composingAlphaAOverB(transform2Grey(scannedImageData), 0.6, [255, 255, 255]);
    // ctx.putImageData(new ImageData(greyData, canvas.width, canvas.height), 0, 0)
    return PIXI.Texture.fromCanvas(canvas, PIXI.SCALE_MODES.NEAREST);
}

function transform2Grey(data) {
    for (let i = 0; i < data.length; i += 4) {
        const [r, g, b] = data.slice(i, i + 3);
        const grey = Math.floor((r + g + b) / 3);
        data[i] = grey;
        data[i + 1] = grey;
        data[i + 2] = grey;
    }
    return data;
}

function composingAlphaAOverB(data, alpha, color: number[]) {
    for (let i = 0; i < data.length; i += 4) {
        const [r, g, b] = data.slice(i, i + 3);
        data[i] = color[0] * alpha + r * 1 * (1 - alpha)
        data[i + 1] = color[1] * alpha + g * 1 * (1 - alpha);
        data[i + 2] = color[2] * alpha + b * 1 * (1 - alpha);
    }
    return data;
}
